/**
 * LanguageDetectionService
 * Handles browser language detection, normalization, and fallback chain
 * Implements ILanguageDetectionService interface for dependency injection
 */

import type { ILanguageDetectionService } from '../../types/i18n';
import {
  DEFAULT_LANGUAGE,
  RTL_LANGUAGES,
  SUPPORTED_LANGUAGES,
  MIN_LANGUAGE_CODE_LENGTH,
} from '../../config/i18n.config';

export class LanguageDetectionService implements ILanguageDetectionService {
  /**
   * Detect the user's preferred language from browser settings
   * @returns Language code (e.g., 'en', 'pt', 'es')
   */
  detectBrowserLanguage(): string {
    try {
      // Get browser language preferences
      const languages = this.getBrowserLanguages();

      // Try each language in order of preference
      for (const lang of languages) {
        const normalized = this.normalizeLanguageCode(lang);
        if (!normalized) continue;

        // Check if we support this language
        const supported = this.getSupportedLanguage(normalized, SUPPORTED_LANGUAGES);
        if (supported) {
          return supported;
        }
      }

      // No supported language found, return default
      return DEFAULT_LANGUAGE;
    } catch (error) {
      console.warn('[LanguageDetection] Error detecting browser language:', error);
      return DEFAULT_LANGUAGE;
    }
  }

  /**
   * Normalize language code to base language (e.g., 'pt-BR' -> 'pt')
   * @param code Language code to normalize
   * @returns Normalized language code
   */
  normalizeLanguageCode(code: string): string {
    if (!code || typeof code !== 'string') {
      return '';
    }

    try {
      // Convert to lowercase and trim
      let normalized = code.toLowerCase().trim();

      // Handle underscores (pt_BR -> pt-BR)
      normalized = normalized.replace('_', '-');

      // Extract base language (before first dash or space)
      const parts = normalized.split(/[-\s]/);
      const baseLanguage = parts[0];

      // Validate base language (should be 2-3 characters)
      if (
        baseLanguage &&
        baseLanguage.length >= MIN_LANGUAGE_CODE_LENGTH &&
        baseLanguage.length <= 3
      ) {
        return baseLanguage;
      }

      return '';
    } catch (error) {
      console.warn('[LanguageDetection] Error normalizing language code:', code, error);
      return '';
    }
  }

  /**
   * Check if a language is supported and return the best match
   * @param code Language code to check
   * @param supportedLanguages List of supported language codes
   * @returns Supported language code or null
   */
  getSupportedLanguage(code: string, supportedLanguages: string[]): string | null {
    if (!code || !supportedLanguages || supportedLanguages.length === 0) {
      return null;
    }

    const normalized = this.normalizeLanguageCode(code);
    if (!normalized) {
      return null;
    }

    // Check for exact match (case-insensitive)
    const exactMatch = supportedLanguages.find(
      (lang) => lang.toLowerCase() === normalized.toLowerCase()
    );
    if (exactMatch) {
      return exactMatch;
    }

    // No match found
    return null;
  }

  /**
   * Get fallback chain for a language (e.g., 'pt-BR' -> ['pt-BR', 'pt', 'en'])
   * @param code Language code
   * @returns Array of language codes in fallback order
   */
  getLanguageFallbackChain(code: string): string[] {
    const chain: string[] = [];

    if (!code || typeof code !== 'string') {
      return [DEFAULT_LANGUAGE];
    }

    // Add original code (with regional variant if present)
    const original = code.toLowerCase().trim();
    if (original && original !== DEFAULT_LANGUAGE) {
      chain.push(original);
    }

    // Add base language (without regional variant)
    const base = this.normalizeLanguageCode(code);
    if (base && base !== original && base !== DEFAULT_LANGUAGE) {
      chain.push(base);
    }

    // Add default language (English) if not already in chain
    if (!chain.includes(DEFAULT_LANGUAGE)) {
      chain.push(DEFAULT_LANGUAGE);
    }

    return chain;
  }

  /**
   * Check if a language uses Right-To-Left text direction
   * @param code Language code
   * @returns True if RTL language
   */
  isRTLLanguage(code: string): boolean {
    if (!code) {
      return false;
    }

    const normalized = this.normalizeLanguageCode(code);
    return RTL_LANGUAGES.includes(normalized);
  }

  /**
   * Get text direction for a language
   * @param code Language code
   * @returns 'rtl' or 'ltr'
   */
  getLanguageDirection(code: string): 'ltr' | 'rtl' {
    return this.isRTLLanguage(code) ? 'rtl' : 'ltr';
  }

  /**
   * Detect language with complete fallback chain
   * Priority: 1) Preferred locale exact match, 2) Preferred fallback chain, 3) Browser language, 4) Default
   * @param preferredLocale Optional preferred language code
   * @returns Language code
   */
  detectLanguageWithFallback(preferredLocale?: string): string {
    // 1. Check preferred locale exact match (from user selection)
    if (preferredLocale) {
      const supported = this.getSupportedLanguage(preferredLocale, SUPPORTED_LANGUAGES);
      if (supported) {
        return supported;
      }

      // 2. Try fallback chain for preferred locale (e.g., en-AU -> en)
      const chain = this.getLanguageFallbackChain(preferredLocale);
      for (const lang of chain) {
        // Skip default language in chain to check browser first
        if (lang === DEFAULT_LANGUAGE) continue;

        const supported = this.getSupportedLanguage(lang, SUPPORTED_LANGUAGES);
        if (supported) {
          return supported;
        }
      }
    }

    // 3. Check browser language (only if no preferred locale match found)
    const browserLang = this.detectBrowserLanguage();
    if (browserLang && browserLang !== DEFAULT_LANGUAGE) {
      return browserLang;
    }

    // 4. Return default locale
    return DEFAULT_LANGUAGE;
  }

  /**
   * Get browser language preferences in order
   * @private
   */
  private getBrowserLanguages(): readonly string[] {
    try {
      // Try navigator.languages first (array of preferences)
      if (
        typeof navigator !== 'undefined' &&
        navigator.languages &&
        Array.isArray(navigator.languages) &&
        navigator.languages.length > 0
      ) {
        return navigator.languages;
      }

      // Fallback to navigator.language (single language)
      if (typeof navigator !== 'undefined' && navigator.language) {
        return [navigator.language];
      }

      // No browser language available
      return [];
    } catch (error) {
      console.warn('[LanguageDetection] Error accessing navigator languages:', error);
      return [];
    }
  }
}

// Export singleton instance for convenience (but allow injection for testing)
export const languageDetectionService = new LanguageDetectionService();
